// @ts-strict-ignore
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, OnInit, Signal, viewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { QuillEditorComponent } from 'ngx-quill';
import { ValidatorFactory } from '@shared/validator.factory';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { SharedModule } from '@shared/shared.module';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { FloatRangeVO, IntRangeVO, PropertyValueVO, PropertyVO } from '@project/components/article/model/vo/property.vo';
import { PropertyValueType } from '@project/constants/property-value-type';
import { ConnectionAreaVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ProjectModel } from '@project/model/project.model';
import { PropertyTypes } from '../properties-table/property-types.enum';
import { CustomValidators } from '@shared/custom-validators';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { IOperation } from '@shared/undo/operation/i-operation';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { PropertyTemplateService } from '../service/property-template.service';
import { PropertyTemplates, PropertyTemplateVO } from '@project/components/article/model/vo/property-template-vo';
import { ConnectionUtils } from '@3d-scene/model3d/util/connection-utils';
import { AdminService } from '../../../services/admin.service';
import { AdmintoolOperationFactory } from '../../../operation/admintool-operation.factory';
import Quill from 'quill';
import { LabelEditorUtils } from '@project/components/label/label-editor/label-editor-utils';
import { MetaElementBehaviorPropertyDef } from '@project/constants/meta-element-behavior-property-def';
import { PropertyUtils } from '@article/util/property-utils';

const FONT_SIZES_PX = ['8px', '9px', '10px', '11px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '36px', '48px', '72px'];
const SizeStyle = Quill.import('attributors/style/size') as any;
SizeStyle.whitelist = FONT_SIZES_PX;
Quill.register(SizeStyle, true);

const ROTATION_VALUES = ['rotate(90deg)', 'rotate(180deg)', 'rotate(270deg)'];
const VERTICAL_ALIGN_VALUES = ['top', 'middle', 'bottom', 'super', 'sub'];
const { StyleAttributor, Scope } = Quill.import('parchment') as any;
const RotationStyle = new StyleAttributor('rotation', 'transform', {
  scope: Scope.INLINE,
  whitelist: ROTATION_VALUES,
});
Quill.register({ 'formats/rotation': RotationStyle }, true);
const VerticalAlignStyle = new StyleAttributor('valign', 'vertical-align', {
  scope: Scope.INLINE,
  whitelist: VERTICAL_ALIGN_VALUES,
});
Quill.register({ 'formats/valign': VerticalAlignStyle }, true);

// Style-based font attributor â€” allows any font-family as inline style
const FontStyle = Quill.import('attributors/style/font') as any;
FontStyle.whitelist = null; // accept any value
Quill.register(FontStyle, true);

const SYMBOL_CHARS: string[] = (() => {
  const result: string[] = [];
  const ranges: (number | number[])[] = [
    0xe011, [0xe070, 0xe073], [0xe01e, 0xe01f], 0xe062, 0xe027,
    [0xe021, 0xe022], 0xe06c, [0xe069, 0xe06b], 0x252c, 0x2534,
    [0xe023, 0xe026], 0xe060, [0xe065, 0xe068], [0xe000, 0xe010],
    [0xe012, 0xe017], [0xe019, 0xe01c], 0xe030, [0xe040, 0xe044],
    [0xe049, 0xe04a], 0xe04e, [0xe057, 0xe05c], 0x03a9,
  ];
  for (const r of ranges) {
    if (Array.isArray(r)) {
      for (let i = r[0]; i <= r[1]; i++) result.push(String.fromCodePoint(i));
    } else {
      result.push(String.fromCodePoint(r));
    }
  }
  return result;
})();

@Component({
  selector: 'app-edit-property-modal',
  templateUrl: './edit-property-modal.component.html',
  styleUrls: ['edit-property-modal.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, SharedModule, QuillEditorComponent],
})
export class EditPropertyModalComponent implements OnInit {
  private quillEditorInstance: any = null;
  private readonly firstPropertyInputEl: Signal<ElementRef | undefined> = viewChild('firstPropertyInputEl', { read: ElementRef });

  private defaultTextRotationDegrees: number | null = null;
  private hasPendingDefaultTextRotationUpdate: boolean = false;
  private defaultTextVerticalAlignmentValue: number | null = null;
  private hasPendingDefaultTextVerticalAlignmentUpdate: boolean = false;

  public readonly quillModules = {
    toolbar: {
      container: '#quill-toolbar',
      handlers: {
        size: (value: any) => {
          if (!this.quillEditorInstance) return;
          const length = this.quillEditorInstance.getLength();
          this.quillEditorInstance.formatText(0, length, 'size', value, 'user');
        },
        color: (value: any) => {
          if (!this.quillEditorInstance) return;
          const length = this.quillEditorInstance.getLength();
          this.quillEditorInstance.formatText(0, length, 'color', value, 'user');
        },
        rotation: (value: any) => {
          const rotationDegrees = value ? Number.parseInt(value) : 0;
          this.trackDefaultTextRotation(rotationDegrees);
          if (!this.quillEditorInstance) return;
          const length = this.quillEditorInstance.getLength();
          this.quillEditorInstance.formatText(
            0,
            length,
            'rotation',
            rotationDegrees,
            'user'
          );
        },
        align: (value: any) => {
          if (!this.quillEditorInstance) return;
          const length = this.quillEditorInstance.getLength();
          this.quillEditorInstance.formatLine(0, length, 'align', value, 'user');
        },
        valign: (value: any) => {
          const verticalAlignmentValue = value ? Number.parseInt(value) : 0;
          this.trackDefaultTextVerticalAlignment(verticalAlignmentValue);
          if (!this.quillEditorInstance) return;
          const length = this.quillEditorInstance.getLength();
          this.quillEditorInstance.formatText(
            0,
            length,
            'valign',
            verticalAlignmentValue,
            'user'
          );
        },
      },
    },
  };

  public onQuillEditorCreated(editor: any): void {
    this.quillEditorInstance = editor;
    const toolbar = editor.getModule('toolbar');
    if (!toolbar?.container) return;

    setTimeout(() => {
      this.syncToolbarFromLegacyHtml();
      if (this.propertyName === MetaElementBehaviorPropertyDef.DEFAULT_TEXT && !this.hasDefaultTextStyles(this.currentDefaultTextHtml)) {
        this.applyDefaultTextStyles();
      }
    });
    if (!this.addNewProperty) {
      setTimeout(() => editor.focus());
    }

    // Build symbol select manually and append to toolbar.
    // We cannot rely on Quill's toolbar handler system for custom formats because
    // attach() runs before handlers from options are registered in Quill 2.x,
    // causing the early-return guard to skip adding the change listener entirely.
    const group = document.createElement('span');
    group.classList.add('ql-formats');

    const select = document.createElement('select');
    select.classList.add('ql-symbol', 'labelEditorSymbolSelection');
    select.title = 'Special characters';

    // Empty placeholder option (no insertion triggered)
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = SYMBOL_CHARS[0]; // show first symbol as the control icon
    select.appendChild(placeholder);

    SYMBOL_CHARS.forEach(char => {
      const opt = document.createElement('option');
      opt.value = char;
      opt.textContent = char;
      select.appendChild(opt);
    });

    select.addEventListener('change', () => {
      const value = select.value;
      select.value = ''; // reset immediately so next pick always fires change
      if (!value || !this.quillEditorInstance) return;
      this.quillEditorInstance.focus();
      const range = this.quillEditorInstance.getSelection(true) ?? { index: 0, length: 0 };
      this.quillEditorInstance.deleteText(range.index, range.length, 'user');
      this.quillEditorInstance.insertText(range.index, value, { font: 'din1451M' }, 'user');
      this.quillEditorInstance.setSelection(range.index + value.length, 0);
    });

    group.appendChild(select);
    toolbar.container.appendChild(group);
  }

  public readonly PropertyTypes = PropertyTypes;
  private readonly ALLOW_ALL_RESTRICTER: string = '.*';
  private NUMBER_RESTRICTER: string = '^-?[0-9]*$';
  private readonly FLOAT_NUMBER_RESTRICTER: string = '^-?[0-9.,]*$';

  public property: PropertyVO;
  public propertyType: string;
  public metaElement: MetaElementVO;
  public finish = new EventEmitter<boolean>();
  public article: ArticleVO;

  private oldProperty: PropertyVO;
  public selectedPropertyValue: PropertyValueVO;
  public addNewProperty: boolean = true;
  public propertyNames: string[] = [];
  public valueTypes: any[];
  public connectionAreaVO: MetaElementVO;
  public selectedPropertyDescription: string;
  public showGiving: boolean = true;
  public showAccepting: boolean = true;
  public showPlausi: boolean = true;
  private originalSelectedPropertyType: string;
  public propertyValueError: string = '';
  private currentDefaultTextHtml: string = '';
  private connectionProperties: PropertyTemplateVO[] = [];
  private behaviorProperties: PropertyTemplateVO[] = [];
  public plausiProperties: PropertyTemplateVO[] = [];
  public inputValueRestricter = this.ALLOW_ALL_RESTRICTER;

  public form: FormGroup;

  constructor(
    private formBuilder: FormBuilder,
    private cdRef: ChangeDetectorRef,
    private bsModalRef: BsModalRef,
    private alertHelper: AlertHelper,
    private localeResourceManager: LocaleResourceManager,
    private propertyTemplateService: PropertyTemplateService,
    private projectModel: ProjectModel,
    private undoRedoHelper: UndoRedoHelper,
    private connectionUtils: ConnectionUtils,
    private adminService: AdminService,
    private admintoolOperationFactory: AdmintoolOperationFactory,
    private validatorFactory: ValidatorFactory,
  ) {}

  public ngOnInit(): void {
    this.updateNumberRestricter();
    this.form = this.formBuilder.group({
      propertyType: [this.projectModel.isCompositeAdminView ? PropertyTypes.PLAUSI : PropertyTypes.BEHAVIOR, [CustomValidators.trimRequired]],
      propertyName: ['', [CustomValidators.trimRequired]],
      propertyValueType: ['', [CustomValidators.trimRequired]],
      firstPropertyInput: [''],
      secondPropertyInput: [''],
    });
    this.propertyTemplateService.getAllPropertyTemplates().subscribe((propertyTemplates: PropertyTemplates) => {
      this.connectionProperties = propertyTemplates.connectionProperties;
      this.behaviorProperties = propertyTemplates.behaviorProperties;
      this.plausiProperties = propertyTemplates.plausiProperties;

      this.initForm();
      setTimeout(() => this.focusFirstValueInput());
    });
  }

  private focusFirstValueInput(): void {
    if (this.addNewProperty) {
      return;
    }
    // defaultText uses Quill – focus is handled in onQuillEditorCreated
    if (this.propertyName === MetaElementBehaviorPropertyDef.DEFAULT_TEXT) {
      return;
    }
    this.firstPropertyInputEl()?.nativeElement?.focus();
  }

  public initForm(): void {
    if (this.property) {
      this.initEditPropertyForm();
    } else {
      this.initCreatePropertyForm();
    }
    if (this.metaElement) {
      this.initMetaElementPropertyForm();
    } else if (this.article) {
      this.initArticlePropertyForm();
    }
    this.updatePropertyValueTypesAndDescription();
  }

  private initArticlePropertyForm() {
    this.showGiving = true;
    this.showAccepting = true;
    this.showPlausi = true;
    this.excludeAlreadyExistingProperties(
      this.article.behaviorProperties,
      this.article.plausiProperties,
      this.article.connectionFeatures,
      this.article.connectionRequirements
    );
    this.switchPropertyNames();
  }

  private initMetaElementPropertyForm() {
    this.connectionAreaVO = this.metaElement;
    if (this.metaElement instanceof ConnectionAreaVO) {
      if ((this.connectionAreaVO as ConnectionAreaVO).connectionSide === ConnectionAreaVO.SIDE_ACCEPTING) {
        this.showGiving = false;
      } else if ((this.connectionAreaVO as ConnectionAreaVO).connectionSide === ConnectionAreaVO.SIDE_GIVING) {
        this.showAccepting = false;
      }
      this.excludeAlreadyExistingProperties(
        this.connectionAreaVO.behaviorProperties,
        (this.connectionAreaVO as ConnectionAreaVO).connectionPlausiProperties,
        (this.connectionAreaVO as ConnectionAreaVO).connectionFeatures,
        (this.connectionAreaVO as ConnectionAreaVO).connectionRequirements
      );
    } else {
      this.showGiving = false;
      this.showAccepting = false;
      this.showPlausi = false;
      this.excludeAlreadyExistingProperties(
        this.connectionAreaVO.behaviorProperties,
        (this.connectionAreaVO as ConnectionAreaVO).connectionPlausiProperties,
        (this.connectionAreaVO as ConnectionAreaVO).connectionFeatures,
        (this.connectionAreaVO as ConnectionAreaVO).connectionRequirements
      );
      this.switchPropertyNames();
    }
  }

  private initCreatePropertyForm() {
    this.property = new PropertyVO();
    const values: PropertyValueVO[] = [];
    this.property.values = values;
    if (this.metaElement && this.metaElement instanceof ConnectionAreaVO) {
      this.form.controls.propertyType.setValue(this.metaElement.connectionSide);
    }
    this.switchPropertyNames();
  }

  private initEditPropertyForm() {
    this.addNewProperty = false;
    if (this.propertyType === PropertyTypes.BEHAVIOR) {
      this.addPropertyNameToList(this.property, this.behaviorProperties);
    } else if (this.propertyType === PropertyTypes.PLAUSI) {
      this.addPropertyNameToList(this.property, this.plausiProperties);
    } else {
      this.addPropertyNameToList(this.property, this.connectionProperties);
    }
    this.form.controls.propertyType.setValue(this.propertyType);
    this.originalSelectedPropertyType = this.propertyType;
    this.switchPropertyNames();
    if (this.property?.values && this.property.values.length > 0) {
      this.toggleSelectedPropertyValue(this.property.values[0]);
    }
  }

  private excludeAlreadyExistingProperties(
    behaviorProperties: PropertyVO[],
    plausiProperties: PropertyVO[],
    featureProperties: PropertyVO[],
    requirementProperties: PropertyVO[]
  ): void {
    const behaviorPropertiesCopyArray: PropertyVO[] = behaviorProperties ? behaviorProperties.concat() : [];
    const plausiPropertiesCopyArray: PropertyVO[] = plausiProperties ? plausiProperties.concat() : [];
    let connectionPropertiesCopyArray: PropertyVO[];

    if (featureProperties && requirementProperties) {
      connectionPropertiesCopyArray = [...featureProperties.concat(), ...requirementProperties.concat()];
    } else if (featureProperties) {
      connectionPropertiesCopyArray = featureProperties.concat();
    } else if (requirementProperties) {
      connectionPropertiesCopyArray = requirementProperties.concat();
    } else {
      connectionPropertiesCopyArray = [];
    }

    if (!this.addNewProperty) {
      const selectedPropertyType: string = this.form.controls.propertyType.value;
      const selectedPropertyName: string = this.form.controls.propertyName.value;
      if (selectedPropertyType === PropertyTypes.BEHAVIOR) {
        const index: number = behaviorPropertiesCopyArray.map((e) => e.name).indexOf(selectedPropertyName);
        if (index !== -1) {
          behaviorPropertiesCopyArray.splice(index, 1);
        }
      } else if (selectedPropertyType === PropertyTypes.PLAUSI) {
        const index: number = plausiPropertiesCopyArray.map((e) => e.name).indexOf(selectedPropertyName);
        if (index !== -1) {
          plausiPropertiesCopyArray.splice(index, 1);
        }
      } else {
        const index: number = connectionPropertiesCopyArray.map((e) => e.name).indexOf(selectedPropertyName);
        if (index !== -1) {
          connectionPropertiesCopyArray.splice(index, 1);
        }
      }
    }
    this.behaviorProperties = this.behaviorProperties.filter(
      (p) => !this.isPropertyNameInPropertiesExist(p.propertyName, behaviorPropertiesCopyArray)
    );
    this.plausiProperties = this.plausiProperties.filter((p) => !this.isPropertyNameInPropertiesExist(p.propertyName, plausiPropertiesCopyArray));
    this.connectionProperties = this.connectionProperties.filter(
      (p) => !this.isPropertyNameInPropertiesExist(p.propertyName, connectionPropertiesCopyArray)
    );
  }

  private isPropertyNameInPropertiesExist(propertyName: string, properties: PropertyVO[]): boolean {
    if (properties?.length) {
      return properties.map((entry) => entry.name).includes(propertyName);
    }
    return false;
  }

  public get propertyName() {
    return this.form.controls.propertyName.value;
  }

  public switchPropertyNames(): void {
    this.savePropertyRevision();
    if (this.metaElement) {
      this.propertyNames = this.connectionProperties.map((entry) => entry.propertyName);
    } else {
      const selectedPropertyType: string = this.form.controls.propertyType.value;
      if (selectedPropertyType === PropertyTypes.BEHAVIOR) {
        this.propertyNames = this.behaviorProperties.map((entry) => entry.propertyName);
      } else if (selectedPropertyType === PropertyTypes.PLAUSI) {
        this.propertyNames = this.plausiProperties.map((entry) => entry.propertyName);
      } else {
        this.propertyNames = this.connectionProperties.map((entry) => entry.propertyName);
      }
    }

    this.property ? this.form.controls.propertyName.setValue(this.property.name) : this.form.controls.propertyName.setValue('');
    this.cdRef.markForCheck();
  }

  public updatePropertyValueTypesAndDescription(): void {
    this.savePropertyRevision();
    this.valueTypes = [];
    let property: PropertyTemplateVO;

    const selectedPropertyType: string = this.form.controls.propertyType.value;
    const selectedPropertyName: string = this.form.controls.propertyName.value;
    if (this.metaElement) {
      property = this.connectionProperties.find((value) => value.propertyName === selectedPropertyName);
    } else if (selectedPropertyType === PropertyTypes.BEHAVIOR) {
      property = this.behaviorProperties.find((value) => value.propertyName === selectedPropertyName);
    } else if (selectedPropertyType === PropertyTypes.PLAUSI) {
      property = this.plausiProperties.find((value) => value.propertyName === selectedPropertyName);
    } else {
      property = this.connectionProperties.find((value) => value.propertyName === selectedPropertyName);
    }

    if (property) {
      if (property.propertyDescription) {
        this.selectedPropertyDescription = property.propertyDescription;
      } else {
        this.selectedPropertyDescription = '';
      }

      property.propertyValueTypes.forEach((type: PropertyValueType) => {
        switch (type) {
          case PropertyValueType.INT:
            this.valueTypes.push({ value: PropertyValueType.INT, name: this.localeResourceManager.getValueByKey('valueType.integer') });
            break;
          case PropertyValueType.FLOAT:
            this.valueTypes.push({ value: PropertyValueType.FLOAT, name: this.localeResourceManager.getValueByKey('valueType.float') });
            break;
          case PropertyValueType.STRING:
            this.valueTypes.push({ value: PropertyValueType.STRING, name: this.localeResourceManager.getValueByKey('valueType.string') });
            break;
          case PropertyValueType.INTRANGE:
            this.valueTypes.push({ value: PropertyValueType.INTRANGE, name: this.localeResourceManager.getValueByKey('valueType.intRange') });
            break;
          case PropertyValueType.FLOATRANGE:
            this.valueTypes.push({ value: PropertyValueType.FLOATRANGE, name: this.localeResourceManager.getValueByKey('valueType.floatRange') });
            break;
          default:
            break;
        }
      });
    }

    if (this.addNewProperty
        && selectedPropertyName === MetaElementBehaviorPropertyDef.DEFAULT_TEXT
        && this.valueTypes.some((vt) => vt.value === PropertyValueType.STRING)) {
      this.form.controls.propertyValueType.setValue(PropertyValueType.STRING);
    }

    if (this.valueTypes.map((vt) => vt.value).includes(this.form.controls.propertyValueType.value)) {
      this.updateValueInputValidation();
    } else if (this.valueTypes.length === 1) {
      this.form.controls.propertyValueType.setValue(this.valueTypes[0].value);
      this.initialPropertyValueTypeChanged();
    } else {
      this.form.controls.propertyValueType.setValue('');
      this.form.controls.propertyValueType.markAsUntouched();
      this.form.controls.firstPropertyInput.setValue('');
      this.form.controls.firstPropertyInput.markAsUntouched();
      this.form.controls.secondPropertyInput.setValue('');
      this.form.controls.secondPropertyInput.markAsUntouched();
      this.updateValueInputValidation();
    }
    this.cdRef.detectChanges();
  }

  private addPropertyNameToList(property: PropertyVO, properties: PropertyTemplateVO[]): void {
    const propertyNames = properties.map((entry) => entry.propertyName);
    if (!property.name && propertyNames.includes(property.name)) {
      properties.push({
        propertyType: property.type,
        propertyName: property.name,
        propertyValueTypes: [
          PropertyValueType.INT,
          PropertyValueType.FLOAT,
          PropertyValueType.STRING,
          PropertyValueType.INTRANGE,
          PropertyValueType.FLOATRANGE,
        ],
        propertyDescription: '',
      });
    }
  }

  public validateForm(): boolean {
    if (!this.form.controls.propertyName.value || !this.form.controls.propertyType.value) {
      return false;
    }
    if (!this.property?.values || this.property.values.length < 1) {
      return false;
    }
    return true;
  }

  private async saveConnectionProperty(): Promise<void> {
    this.property.name = this.form.controls.propertyName.value;

    const selectedPropertyType: string = this.form.controls.propertyType.value;
    if (selectedPropertyType === PropertyTypes.BEHAVIOR) {
      this.connectionAreaVO.behaviorProperties.push(this.property);
    } else if (this.connectionAreaVO instanceof ConnectionAreaVO) {
      if (selectedPropertyType === PropertyTypes.PLAUSI) {
        this.connectionAreaVO.connectionPlausiProperties.push(this.property);
      } else if (selectedPropertyType === PropertyTypes.GIVING) {
        this.connectionAreaVO.connectionFeatures.push(this.property);
      } else if (selectedPropertyType === PropertyTypes.ACCEPTING) {
        this.connectionAreaVO.connectionRequirements.push(this.property);
      }
    }

    this.connectionUtils.updateInternalConnectionProperties(this.metaElement, this.article);

    if (this.addNewProperty) {
      const addConnectionPropertyOperation: IOperation = this.admintoolOperationFactory.createConnectionPropertyOperation(
        this.property,
        this.connectionAreaVO.name,
        this.article,
        selectedPropertyType,
        'ADD'
      );
      if (addConnectionPropertyOperation != null) {
        await this.undoRedoHelper.pushUndoOperation(addConnectionPropertyOperation);
      }
    } else {
      const addConnectionPropertyChangeOperation: IOperation = this.admintoolOperationFactory.createConnectionPropertyChangedOperation(
        this.oldProperty,
        this.property,
        selectedPropertyType,
        this.originalSelectedPropertyType,
        this.connectionAreaVO.name,
        this.article
      );
      if (addConnectionPropertyChangeOperation != null) {
        await this.undoRedoHelper.pushUndoOperation(addConnectionPropertyChangeOperation);
      }
    }

    this.projectModel.notifyCurrentProjectChanged();
  }

  private async saveArticleProperty(): Promise<void> {
    const selectedPropertyType: string = this.form.controls.propertyType.value;
    this.property.name = this.form.controls.propertyName.value;
    if (selectedPropertyType === PropertyTypes.BEHAVIOR) {
      this.article.behaviorProperties.push(this.property);
    } else if (selectedPropertyType === PropertyTypes.PLAUSI) {
      this.article.plausiProperties.push(this.property);
    } else if (selectedPropertyType === PropertyTypes.GIVING) {
      this.article.connectionFeatures.push(this.property);
    } else if (selectedPropertyType === PropertyTypes.ACCEPTING) {
      this.article.connectionRequirements.push(this.property);
    }

    if (this.addNewProperty) {
      const addArticlePropertyOperation: IOperation = this.admintoolOperationFactory.createArticlePropertyOperation(
        this.property,
        this.article,
        selectedPropertyType,
        UndoRedoUtils.ADD
      );
      if (addArticlePropertyOperation != null) {
        await this.undoRedoHelper.pushUndoOperation(addArticlePropertyOperation);
      }
    } else {
      const addArticlePropertyChangeOperation: IOperation = this.admintoolOperationFactory.createArticlePropertyChangedOperation(
        this.oldProperty,
        this.property,
        selectedPropertyType,
        this.originalSelectedPropertyType,
        this.article
      );
      if (addArticlePropertyChangeOperation != null) {
        await this.undoRedoHelper.pushUndoOperation(addArticlePropertyChangeOperation);
      }
    }

    this.projectModel.notifyCurrentProjectChanged();
  }

  public addPropertyValue(): void {
    this.savePropertyRevision();
    const newProperty: PropertyValueVO = new PropertyValueVO();
    if (this.form.invalid) {
      return;
    }
    // For the Quill editor (defaultText), read the HTML directly from the DOM to avoid
    // any FormControl sync lag caused by programmatic (non-'user') insertions.
    const firstPropertyInputText: string =
      this.propertyName === 'defaultText' && this.quillEditorInstance
        ? this.quillEditorInstance.root.innerHTML
        : this.form.controls.firstPropertyInput.value;
    const secondPropertyInputText: string = this.form.controls.secondPropertyInput.value;
    newProperty.type = this.form.controls.propertyValueType.value;
    switch (newProperty.type) {
      case PropertyValueType.INT:
        newProperty.intValue = Number.parseInt(this.localeResourceManager.parseNumber(firstPropertyInputText).toString(), 10);
        break;
      case PropertyValueType.FLOAT:
        newProperty.floatValue = this.localeResourceManager.parseNumber(firstPropertyInputText);
        break;
      case PropertyValueType.STRING:
        newProperty.stringValue = this.propertyName === 'defaultText'
          ? this.convertHTML5DataToDataForPrintExport(firstPropertyInputText)
          : firstPropertyInputText;
        break;
      case PropertyValueType.INTRANGE: {
        const intrange: IntRangeVO = new IntRangeVO(
          Number.parseInt(this.localeResourceManager.parseNumber(firstPropertyInputText).toString(), 10),
          Number.parseInt(this.localeResourceManager.parseNumber(secondPropertyInputText).toString(), 10)
        );
        newProperty.intRange = intrange;
        break;
      }
      case PropertyValueType.FLOATRANGE: {
        const floatRange: FloatRangeVO = new FloatRangeVO(
          this.localeResourceManager.parseNumber(firstPropertyInputText),
          this.localeResourceManager.parseNumber(secondPropertyInputText)
        );
        newProperty.floatRange = floatRange;
        break;
      }
    }
    this.property.values.push(newProperty);
    this.cdRef.detectChanges();
  }

  public changePropertyValue(): void {
    this.savePropertyRevision();
    if (!this.selectedPropertyValue || this.form.invalid) {
      return;
    }
    const firstPropertyInputText: string =
      this.propertyName === 'defaultText' && this.quillEditorInstance
        ? this.quillEditorInstance.root.innerHTML
        : this.form.controls.firstPropertyInput.value;
    const secondPropertyInputText: string = this.form.controls.secondPropertyInput.value;
    switch (this.selectedPropertyValue.type) {
      case PropertyValueType.INT:
        this.selectedPropertyValue.intValue = Number.parseInt(this.localeResourceManager.parseNumber(firstPropertyInputText).toString(), 10);
        break;
      case PropertyValueType.FLOAT:
        this.selectedPropertyValue.floatValue = this.localeResourceManager.parseNumber(firstPropertyInputText);
        break;
      case PropertyValueType.STRING:
        this.selectedPropertyValue.stringValue = this.propertyName === 'defaultText'
          ? this.convertHTML5DataToDataForPrintExport(firstPropertyInputText)
          : firstPropertyInputText;
        break;
      case PropertyValueType.INTRANGE: {
        const intrange: IntRangeVO = new IntRangeVO(
          Number.parseInt(this.localeResourceManager.parseNumber(firstPropertyInputText).toString(), 10),
          Number.parseInt(this.localeResourceManager.parseNumber(secondPropertyInputText).toString(), 10)
        );
        this.selectedPropertyValue.intRange = intrange;
        break;
      }
      case PropertyValueType.FLOATRANGE: {
        const floatRange: FloatRangeVO = new FloatRangeVO(
          this.localeResourceManager.parseNumber(firstPropertyInputText),
          this.localeResourceManager.parseNumber(secondPropertyInputText)
        );
        this.selectedPropertyValue.floatRange = floatRange;
        break;
      }
    }
    this.resetEditValue();
    this.cdRef.detectChanges();
  }

  private convertHTML5DataToDataForPrintExport(htmlString: string): string {
    return LabelEditorUtils.convertHTML5DataToData(htmlString).replace(/FACE="undefined"/, 'FACE="din1451M"');
  }

  public resetEditValue(): void {
    this.form.controls.firstPropertyInput.setValue('');
    this.form.controls.firstPropertyInput.markAsUntouched();
    this.form.controls.secondPropertyInput.setValue('');
    this.form.controls.secondPropertyInput.markAsUntouched();
    this.selectedPropertyValue = null;
  }

  public refreshView(): void {
    this.cdRef.detectChanges();
  }

  public deletePropertyValue(propertyValue: PropertyValueVO, i: number): void {
    this.savePropertyRevision();
    if (this.selectedPropertyValue === propertyValue) {
      this.resetEditValue();
    }
    this.property.values.splice(i, 1);
    this.cdRef.detectChanges();
  }

  public isSecondInputHidden(type: string): boolean {
    if (type === PropertyValueType.INTRANGE || type === PropertyValueType.FLOATRANGE) {
      return false;
    }
    return true;
  }

  public close(closeTypeSuccess: boolean = false): void {
    this.bsModalRef.hide();
    this.finish.emit(closeTypeSuccess);
  }

  public propertyValueTypeChangedHandler(): void {
    this.savePropertyRevision();
    const selectedPropertyValue: PropertyValueVO = this.selectedPropertyValue;
    const selectedPropertyValueType: string = this.form.controls.propertyValueType.value;
    const firstInput: string = this.form.controls.firstPropertyInput.value;
    const secondInput: string = this.form.controls.secondPropertyInput.value;
    this.resetEditValue();
    if (this.property?.values && this.property.values.length > 0) {
      if (selectedPropertyValueType && selectedPropertyValueType !== this.property.values[0].type) {
        this.alertHelper
          .showAlert(
            'editPropertyPopup.changepropertyValueTypeconfirm.title',
            'editPropertyPopup.changepropertyValueTypeconfirm.message',
            null,
            [AlertButton.OK, AlertButton.CANCEL],
            "error"
          )
          .then((answer) => {
            if (answer === AlertButton.OK) {
              this.property.values = [];
            } else {
              this.form.controls.propertyValueType.setValue(this.property.values[0].type);
              this.selectedPropertyValue = selectedPropertyValue;
              this.form.controls.firstPropertyInput.setValue(firstInput);
              this.form.controls.secondPropertyInput.setValue(secondInput);
              this.updateValueInputValidation();
            }
          });
      }
    }
    this.updateValueInputValidation();
  }

  public initialPropertyValueTypeChanged(): void {
    const selectedPropertyValue: PropertyValueVO = this.selectedPropertyValue;
    const selectedPropertyValueType: string = this.form.controls.propertyValueType.value;
    const firstInput: string = this.form.controls.firstPropertyInput.value;
    const secondInput: string = this.form.controls.secondPropertyInput.value;
    this.resetEditValue();

    if (this.property?.values && this.property.values.length > 0) {
      if (selectedPropertyValueType && selectedPropertyValueType !== this.property.values[0].type) {

        this.alertHelper.showAlert(
            'editPropertyPopup.changepropertyValueTypeconfirm.title',
            'editPropertyPopup.changepropertyValueTypeconfirm.message2',
            null,
            [AlertButton.YES, AlertButton.NO],
            "error"
          ).then((answer) => {
            if (answer === AlertButton.YES) {
              this.property.values = [];
            } else {
              this.form.controls.propertyValueType.setValue(this.property.values[0].type);
              this.selectedPropertyValue = selectedPropertyValue;
              this.form.controls.firstPropertyInput.setValue(firstInput);
              this.form.controls.secondPropertyInput.setValue(secondInput);
              this.updateValueInputValidation();
            }
          })
      }
    }
    this.updateValueInputValidation();
  }

  public getType(value: PropertyValueVO): string {
    switch (value.type) {
      case PropertyValueType.INT:
        return this.localeResourceManager.getValueByKey('valueType.integer');
      case PropertyValueType.FLOAT:
        return this.localeResourceManager.getValueByKey('valueType.float');
      case PropertyValueType.STRING:
        return this.localeResourceManager.getValueByKey('valueType.string');
      case PropertyValueType.INTRANGE:
        return this.localeResourceManager.getValueByKey('valueType.intRange');
      case PropertyValueType.FLOATRANGE:
        return this.localeResourceManager.getValueByKey('valueType.floatRange');
    }
    return '';
  }

  public getValue(value: PropertyValueVO): string {
    return value.toString(this.localeResourceManager);
  }

  /** For defaultText, strips HTML tags and returns plain text so symbols can be
   *  displayed in din1451M without rendering raw HTML markup in the table. */
  public getDisplayValue(value: PropertyValueVO): string {
    return this.getValue(value).toString();
  }

  public async saveClickHandler(): Promise<void> {
    if (!this.validateForm()) {
      return;
    }
    const shouldUpdateDefaultTextRotation = this.shouldWriteDefaultTextRotationProperty();
    if (shouldUpdateDefaultTextRotation) {
      this.writeDefaultTextRotationProperty();
    }
    const shouldUpdateDefaultTextVerticalAlignment = this.shouldWriteDefaultTextVerticalAlignmentProperty();
    if (shouldUpdateDefaultTextVerticalAlignment) {
      this.writeDefaultTextVerticalAlignmentProperty();
    }
    if (this.addNewProperty) {
      if (this.metaElement) {
        await this.saveConnectionProperty();
      } else if (this.article) {
        await this.saveArticleProperty();
      }
    } else if (this.originalSelectedPropertyType !== this.property.type) {
      if (this.metaElement) {
        this.removeCurrentProperty();
        await this.saveConnectionProperty();
      } else if (this.article) {
        this.removeCurrentProperty();
        await this.saveArticleProperty();
      }
    }
    this.close();
    const updatedPropertyNames: string[] = [this.property.name];
    if (shouldUpdateDefaultTextRotation) {
      updatedPropertyNames.push(MetaElementBehaviorPropertyDef.DEFAULT_TEXT_ROTATION);
    }
    if (shouldUpdateDefaultTextVerticalAlignment) {
      updatedPropertyNames.push(MetaElementBehaviorPropertyDef.DEFAULT_TEXT_VERTICAL_ALIGNMENT);
    }
    this.adminService.updateProperties(updatedPropertyNames);
  }

  private shouldWriteDefaultTextRotationProperty(): boolean {
    return this.hasPendingDefaultTextRotationUpdate
      && this.propertyName === MetaElementBehaviorPropertyDef.DEFAULT_TEXT
      && this.defaultTextRotationDegrees !== null;
  }

  private trackDefaultTextRotation(rotationDegrees: number): void {
    this.defaultTextRotationDegrees = rotationDegrees;
    this.hasPendingDefaultTextRotationUpdate = true;
  }

  private writeDefaultTextRotationProperty(): void {
    const behaviorProperties: PropertyVO[] = this.getBehaviorPropertiesForRotationUpdate();
    if (!behaviorProperties || this.defaultTextRotationDegrees === null) {
      return;
    }

    PropertyUtils.setIntPropertyValue(behaviorProperties, MetaElementBehaviorPropertyDef.DEFAULT_TEXT_ROTATION, this.defaultTextRotationDegrees);
    this.hasPendingDefaultTextRotationUpdate = false;
  }

  private getBehaviorPropertiesForRotationUpdate(): PropertyVO[] {
    if (this.metaElement) {
      if (!this.connectionAreaVO.behaviorProperties) {
        this.connectionAreaVO.behaviorProperties = [];
      }
      return this.connectionAreaVO.behaviorProperties;
    }
    if (this.article) {
      if (!this.article.behaviorProperties) {
        this.article.behaviorProperties = [];
      }
      return this.article.behaviorProperties;
    }
    return null;
  }

  private shouldWriteDefaultTextVerticalAlignmentProperty(): boolean {
    return this.hasPendingDefaultTextVerticalAlignmentUpdate
      && this.propertyName === MetaElementBehaviorPropertyDef.DEFAULT_TEXT
      && this.defaultTextVerticalAlignmentValue !== null;
  }

  private trackDefaultTextVerticalAlignment(verticalAlignmentValue: number): void {
    this.defaultTextVerticalAlignmentValue = verticalAlignmentValue;
    this.hasPendingDefaultTextVerticalAlignmentUpdate = true;
  }

  private writeDefaultTextVerticalAlignmentProperty(): void {
    const behaviorProperties: PropertyVO[] = this.getBehaviorPropertiesForRotationUpdate();
    if (!behaviorProperties || this.defaultTextVerticalAlignmentValue === null) {
      return;
    }

    PropertyUtils.setIntPropertyValue(behaviorProperties, MetaElementBehaviorPropertyDef.DEFAULT_TEXT_VERTICAL_ALIGNMENT, this.defaultTextVerticalAlignmentValue);
    this.hasPendingDefaultTextVerticalAlignmentUpdate = false;
  }

  public toggleSelectedPropertyValue(value: PropertyValueVO): void {
    if (this.selectedPropertyValue === value) {
      this.resetEditValue();
    } else {
      this.selectedPropertyValue = value;
      this.form.controls.propertyValueType.setValue(value.type);
      switch (value.type) {
        case PropertyValueType.INT:
        case PropertyValueType.FLOAT:
          this.form.controls.firstPropertyInput.setValue(value.toString(this.localeResourceManager));
          break;
        case PropertyValueType.STRING:
          if (this.propertyName === 'defaultText') {
            this.currentDefaultTextHtml = value.stringValue ?? '';
            this.form.controls.firstPropertyInput.setValue(this.convertDataForPrintExportToHTML5Data(this.currentDefaultTextHtml));
            setTimeout(() => {
              this.syncToolbarFromLegacyHtml();
              if (!this.hasDefaultTextStyles(this.currentDefaultTextHtml)) {
                this.applyDefaultTextStyles();
              }
            });
          } else {
            this.form.controls.firstPropertyInput.setValue(value.toString(this.localeResourceManager));
          }
          break;
        case PropertyValueType.INTRANGE:
          this.form.controls.firstPropertyInput.setValue(this.localeResourceManager.formatNumber(value.intRange.left));
          this.form.controls.secondPropertyInput.setValue(this.localeResourceManager.formatNumber(value.intRange.right));
          break;
        case PropertyValueType.FLOATRANGE:
          this.form.controls.firstPropertyInput.setValue(this.localeResourceManager.formatNumber(value.floatRange.left));
          this.form.controls.secondPropertyInput.setValue(this.localeResourceManager.formatNumber(value.floatRange.right));
          break;
      }
      this.updateValueInputValidation();
    }
  }

  private removeCurrentProperty(): void {
    if (this.metaElement) {
      if (this.originalSelectedPropertyType === PropertyTypes.BEHAVIOR) {
        this.removeProperty(this.connectionAreaVO.behaviorProperties, this.property);
      } else if (this.connectionAreaVO instanceof ConnectionAreaVO) {
        if (this.originalSelectedPropertyType === PropertyTypes.PLAUSI) {
          this.removeProperty(this.connectionAreaVO.connectionPlausiProperties, this.property);
        } else if (this.originalSelectedPropertyType === PropertyTypes.GIVING) {
          this.removeProperty(this.connectionAreaVO.connectionFeatures, this.property);
        } else if (this.originalSelectedPropertyType === PropertyTypes.ACCEPTING) {
          this.removeProperty(this.connectionAreaVO.connectionRequirements, this.property);
        }
      }
    } else if (this.article) {
      if (this.originalSelectedPropertyType === PropertyTypes.BEHAVIOR) {
        this.removeProperty(this.article.behaviorProperties, this.property);
      } else if (this.originalSelectedPropertyType === PropertyTypes.PLAUSI) {
        this.removeProperty(this.article.plausiProperties, this.property);
      } else if (this.originalSelectedPropertyType === PropertyTypes.GIVING) {
        this.removeProperty(this.article.connectionFeatures, this.property);
      } else if (this.originalSelectedPropertyType === PropertyTypes.ACCEPTING) {
        this.removeProperty(this.article.connectionRequirements, this.property);
      }
    }
    this.projectModel.notifyCurrentProjectChanged();
  }

  private removeProperty(properties: PropertyVO[], propertyToDelete: PropertyVO): void {
    if (propertyToDelete && properties) {
      properties.forEach((property: PropertyVO) => {
        if (propertyToDelete.name === property.name) {
          properties.splice(properties.indexOf(property), 1);
        }
      });
    }
  }

  private savePropertyRevision() {
    if (!this.oldProperty && this.property) {
      this.oldProperty = this.property.clone();
    }
  }

  public getInputError(c: AbstractControl): string {
    if (c?.hasError('messageKey')) {
      return this.localeResourceManager.getValueByKey(c.getError('messageKey'), { 0: c.getError('param') });
    } else {
      return '';
    }
  }

  private hasDefaultTextStyles(html: string): boolean {
    if (!html?.trim()) {
      return false;
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const font = doc.querySelector('font');
    const p = doc.querySelector('p');
    return !!(font?.getAttribute('color') || font?.getAttribute('size') || p?.getAttribute('align'));
  }

  private applyDefaultTextStyles(): void {
    if (!this.quillEditorInstance) {
      return;
    }
    const length = this.quillEditorInstance.getLength();
    // Place the cursor at the start of the document.
    // This also focuses the editor so that:
    //  - inline cursor formats (size, color) can be set via quill.format()
    //  - the toolbar reads getFormat(0,0) instead of null and updates correctly.
    this.quillEditorInstance.setSelection(0, 0);
    // Apply inline cursor formats.  quill.format() stores them in the cursor blot,
    // which is returned by getFormat({index:0,length:0}) and therefore reflected
    // in the toolbar after the synchronous EDITOR_CHANGE event.
    this.quillEditorInstance.format('size', '20px');   // CSS 20px = "10px" label
    this.quillEditorInstance.format('color', '#000000');
    // For existing (unstyled) text, also format the content directly.
    if (length > 1) {
      this.quillEditorInstance.formatText(0, length, 'size', '20px', 'user');
      this.quillEditorInstance.formatText(0, length, 'color', '#000000', 'user');
    }
    // Apply center alignment to every line.
    this.quillEditorInstance.formatLine(0, length, 'align', 'center', 'user');
  }

  private syncToolbarFromLegacyHtml(): void {    if (!this.quillEditorInstance) return;
    const toolbar = this.quillEditorInstance.getModule('toolbar');
    if (!toolbar?.container) return;

    const doc = new DOMParser().parseFromString(this.currentDefaultTextHtml, 'text/html');
    const font = doc.querySelector('font');
    const p = doc.querySelector('p');

    const rawSize = font?.getAttribute('size') ?? '';
    const color = font?.getAttribute('color') ?? '';
    const size = rawSize ? `${rawSize}px` : '';
    const align = (p?.getAttribute('align') ?? '').toLowerCase();

    const sizeSelect = toolbar.container.querySelector('.ql-size') as HTMLSelectElement;
    if (sizeSelect) sizeSelect.value = size;

    const colorSelect = toolbar.container.querySelector('.ql-color') as HTMLSelectElement;
    if (colorSelect) colorSelect.value = color;

    const rotationSelect = toolbar.container.querySelector('.ql-rotation') as HTMLSelectElement;
    if (rotationSelect) {
      this.syncQuillPickerSelection(toolbar.container, rotationSelect, this.resolveRotationDropdownValue(), 'ql-rotation');
    }

    const alignSelect = toolbar.container.querySelector('.ql-align') as HTMLSelectElement;
    if (alignSelect) alignSelect.value = align;

    const valignSelect = toolbar.container.querySelector('.ql-valign') as HTMLSelectElement;
    if (valignSelect) {
      this.syncQuillPickerSelection(toolbar.container, valignSelect, this.resolveValignDropdownValue(), 'ql-valign');
    }
  }

  private resolveRotationDropdownValue(): string {
    const parsedRotation: number = this.parseRotationBehaviorValue();
    return `${parsedRotation}`;
  }

  private resolveValignDropdownValue(): string {
    const parsedValign: number = this.parseVerticalAlignBehaviorValue();
    return `${parsedValign}`;
  }

  private parseRotationBehaviorValue(): number {
    const propertyValue: PropertyValueVO | null = this.readBehaviorPropertyValue(
      MetaElementBehaviorPropertyDef.DEFAULT_TEXT_ROTATION
    );
    if (!propertyValue) {
      return 0;
    }

    const rawValue: string = `${propertyValue.intValue ?? propertyValue.stringValue ?? ''}`.trim().replace(/^"(.*)"$/, '$1');
    if (!rawValue) {
      return 0;
    }

    const numericValue: number = Number(rawValue);
    return Number.isFinite(numericValue) ? numericValue : 0;
  }

  private parseVerticalAlignBehaviorValue(): number {
    const propertyValue: PropertyValueVO | null = this.readBehaviorPropertyValue(
      MetaElementBehaviorPropertyDef.DEFAULT_TEXT_VERTICAL_ALIGNMENT
    );
    if (!propertyValue) {
      return 0;
    }

    const rawValue: string = `${propertyValue.intValue ?? propertyValue.stringValue ?? ''}`
      .trim()
      .replace(/^"(.*)"$/, '$1');
    if (!rawValue) {
      return 0;
    }


    const numericValue: number = Number(rawValue);
    return Number.isFinite(numericValue) ? numericValue : 0;
  }

  private readBehaviorPropertyValue(propertyName: string): PropertyValueVO | null {
    const behaviorProperties: PropertyVO[] = this.getBehaviorPropertiesForRotationUpdate();
    if (!behaviorProperties) {
      return null;
    }

    const found: PropertyVO = behaviorProperties.find(
      (p: PropertyVO) => p.name?.toLowerCase() === propertyName.toLowerCase()
    );
    if (!found?.values?.length) {
      return null;
    }

    return found.values[0];
  }

  private syncQuillPickerSelection(
    toolbarContainer: HTMLElement,
    selectElement: HTMLSelectElement,
    value: string,
    pickerClass: string
  ): void {
    selectElement.value = value;

    // Quill does not always refresh custom picker labels when only the hidden select value changes.
    const pickerElement: Element | null = toolbarContainer.querySelector(`.ql-picker.${pickerClass}`);
    if (!pickerElement?.classList?.contains('ql-picker')) {
      return;
    }

    const labelElement: HTMLElement = pickerElement.querySelector('.ql-picker-label');
    if (labelElement) {
      if (value) {
        labelElement.dataset.value = value;
      } else {
        delete labelElement.dataset.value;
      }
    }

    const itemElements: NodeListOf<HTMLElement> = pickerElement.querySelectorAll('.ql-picker-item');
    itemElements.forEach((itemElement: HTMLElement) => {
      const itemValue: string = itemElement.dataset.value ?? '';
      itemElement.classList.toggle('ql-selected', itemValue === value);
    });
  }

  private updateValueInputValidation(): void {    const valueType: string = this.form.controls.propertyValueType.value;
    const numberValidator = this.validatorFactory.number({precision: false});
    switch (valueType) {
      case PropertyValueType.INTRANGE:
      case PropertyValueType.FLOATRANGE:
        this.form.controls.firstPropertyInput.setValidators([numberValidator, CustomValidators.trimRequired]);
        this.form.controls.firstPropertyInput.updateValueAndValidity();
        this.form.controls.secondPropertyInput.setValidators([numberValidator, CustomValidators.trimRequired]);
        this.form.controls.secondPropertyInput.updateValueAndValidity();
        break;
      case PropertyValueType.INT:
      case PropertyValueType.FLOAT:
        this.form.controls.firstPropertyInput.setValidators([numberValidator, CustomValidators.trimRequired]);
        this.form.controls.firstPropertyInput.updateValueAndValidity();
        this.form.controls.secondPropertyInput.setValue('');
        this.form.controls.secondPropertyInput.clearValidators();
        this.form.controls.secondPropertyInput.updateValueAndValidity();
        break;
      case PropertyValueType.STRING:
      default:
        this.form.controls.firstPropertyInput.setValidators([CustomValidators.trimRequired]);
        this.form.controls.firstPropertyInput.updateValueAndValidity();
        this.form.controls.secondPropertyInput.setValue('');
        this.form.controls.secondPropertyInput.clearValidators();
        this.form.controls.secondPropertyInput.updateValueAndValidity();
        break;
    }
    switch (valueType) {
      case PropertyValueType.INT:
      case PropertyValueType.INTRANGE:
        this.inputValueRestricter = this.NUMBER_RESTRICTER;
        break;
      case PropertyValueType.FLOAT:
      case PropertyValueType.FLOATRANGE:
        this.inputValueRestricter = this.NUMBER_RESTRICTER;
        this.inputValueRestricter = this.FLOAT_NUMBER_RESTRICTER;
        break;
      case PropertyValueType.STRING:
      default:
        this.inputValueRestricter = this.ALLOW_ALL_RESTRICTER;
        break;
    }
    this.cdRef.detectChanges();
  }

  private updateNumberRestricter(): void {
    const thousandsSeparator: string = this.localeResourceManager.getValueByKey('validation.number.thousands_separator');
    this.NUMBER_RESTRICTER = `^-?[0-9${thousandsSeparator}]*$`;
  }

  private convertDataForPrintExportToHTML5Data(dataString: string): string {
    const html = LabelEditorUtils.convertDataToHTML5DataDocument(dataString).getElementById('content').innerHTML;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const symbolCharSet = new Set(SYMBOL_CHARS);

    doc.querySelectorAll('span').forEach((span: HTMLSpanElement) => {
      
      const hasSymbol = [...(span.textContent ?? '')].some((char) => symbolCharSet.has(char));
      if (!hasSymbol || span.style.fontFamily) return;

      span.style.fontFamily = 'din1451M';
    });

    return doc.body.innerHTML;
  }
}
